import React, { Dispatch, useEffect, useMemo } from 'react';

import Cookie from 'js-cookie';
import { unmount } from '../../../../../services/reactRoot';
import { createContext } from 'use-context-selector';

import { COOKIE_AST_TOKEN_JS, GOOGLE_AUTH_ID } from '../../../../../constants/Common';

import { IReducerAction } from '../../../../Hooks/Context/connect';
import translateHookTS from '../../../../Hooks/Lang/TranslateHookTS';
import useCombinedReducers from '../hooks/useCombinedReducers';

import { IAuthFunctionalProps } from '../../../../modules/AuthFunctional/AuthFunctional';
import { Steps, EVENTS } from '../Constants';
import { Types as ContainerTypes, actionRenderPayment } from '../Store/ContainerReducer';
import { IAuthState, initialState, RESET_REDUCER } from '../Store/Reducers';

interface IAuthReducerContext {
    store: IAuthState,
    dispatch: Dispatch<IReducerAction>
}

const defaultStore: IAuthReducerContext = {
    store: { ...initialState },
    dispatch: () => null
};

interface IStyledModalEvent {
    (newChildren?: any): void
}

declare global {
    interface Document {
        openStyledModal: Record<string, IStyledModalEvent>,
        closeStyledModal: Record<string, IStyledModalEvent>,
    }
}

type TAuthFunctionalProviderProps = React.PropsWithChildren<IAuthFunctionalProps>;

const AuthFunctionalContext = createContext(defaultStore);

const AuthFunctionalProvider: React.FC<TAuthFunctionalProviderProps> = ({ children, ...props }) => {
    const { store, dispatch } = useCombinedReducers({ ...props });

    const {
        step,
        uuid,
        listenTo,
        initialStep,
        communication,
        redirectToAuth,
        oauthShowConsetTerms
    } = store.Container;

    const { amount } = store.Container.pagarme;

    const onStepChangeEvent = useMemo(() => new CustomEvent(EVENTS.AUTH_FUNCTIONAL_STEP_CHANGED, { detail: { crrStep: step } }), [step]);

    useEffect(() => {
        document.dispatchEvent(onStepChangeEvent);
    }, [onStepChangeEvent, step]);

    useEffect(() => {
        const cookieAuthid = Cookie.get(GOOGLE_AUTH_ID);

        if (cookieAuthid) {
            dispatch({
                type: ContainerTypes.CHANGE_STEP,
                data: Steps.LOGIN
            });
        }
    }, [dispatch, step, initialStep]);

    useEffect(() => {
        const modal = document.querySelector('#styled-modal-container');

        if (oauthShowConsetTerms && modal !== null) {
            if (!modal || !modal.parentElement) return;

            const modalId = modal.parentElement.id || '';

            if (modalId && modal.querySelector('auth-functional')) {
                dispatch({
                    type: ContainerTypes.CHANGE_STEP,
                    data: Steps.LOGIN
                });

                document.openStyledModal[modalId]();
            }
        }
    }, [dispatch, oauthShowConsetTerms]);

    useEffect(() => {
        if (listenTo) {
            document.addEventListener('CloseStyledModal', ((event: CustomEvent) => {
                if (event.detail !== listenTo) return;

                dispatch({ type: RESET_REDUCER });
                const authFunctionalElement = document.querySelector(`[data-uuid="${uuid}"]`);

                if (!authFunctionalElement) return;

                unmount(authFunctionalElement);
            }) as EventListener);
        }

        if (!amount && !communication && redirectToAuth) return;

        const token = Cookie.get(COOKIE_AST_TOKEN_JS);

        if (token) actionRenderPayment(dispatch, token, '');
    }, [dispatch, amount, uuid, listenTo, communication, redirectToAuth]);

    return (
        <AuthFunctionalContext.Provider
            value={{
                store,
                dispatch
            }}
        >
            <>
                {children}
            </>
        </AuthFunctionalContext.Provider>
    );
};

export {
    defaultStore,
    AuthFunctionalContext,

    IStyledModalEvent,
    IAuthReducerContext
};

export default translateHookTS<TAuthFunctionalProviderProps>(AuthFunctionalProvider);
